import React from "react";
import { ScrollText } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";

/**
 * Page Conditions Générales de Vente (CGV) — Signapharma France.
 * Marque LOSTA TECH SAS. Droit français applicable.
 * Version FR = référence contractuelle.
 */

const Section = ({ n, title, children }) => (
  <section className="mb-10" data-testid={`cgv-section-${n}`}>
    <h2 className="font-display text-xl sm:text-2xl font-bold uppercase tracking-wider text-[#39FF14] neon-text-soft mb-4 flex items-baseline gap-3">
      <span className="font-mono text-sm text-zinc-500">{String(n).padStart(2, "0")}.</span>
      {title}
    </h2>
    <div className="text-zinc-300 text-base leading-relaxed font-body space-y-3 pl-10 border-l border-white/5">
      {children}
    </div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-[#39FF14]">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);

export default function CGV() {
  const { lang } = useI18n();

  return (
    <div className="min-h-screen bg-black text-white" data-testid="page-cgv">
      {/* Hero */}
      <section className="pt-28 lg:pt-32 pb-14 border-b border-white/5">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-px bg-[#39FF14]" style={{ boxShadow: "0 0 12px #39FF14" }} />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#39FF14]">
              {lang === "fr" ? "Mentions légales" : "Legal"}
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-[0.95] tracking-tight">
            <ScrollText className="inline-block mb-2 mr-3 text-[#39FF14]" size={42} />
            {lang === "fr" ? "Conditions générales" : "Terms & conditions"}
            <span className="block mt-1 text-zinc-400 text-2xl sm:text-3xl">
              {lang === "fr" ? "de vente (CGV)" : "of sale"}
            </span>
          </h1>
          <p className="mt-6 text-base text-zinc-400 max-w-2xl">
            {lang === "fr"
              ? "Version applicable aux ventes et services réalisés via le site signapharma.fr. Texte original en français — version contractuelle de référence."
              : "Version applicable to all sales made through signapharma.fr. The French version is the legally binding reference text."}
          </p>
        </div>
      </section>

      {/* Contenu */}
      <section className="py-14 lg:py-20">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">

          <Section n={1} title="Société">
            <p>Le site Signapharma France est exploité par :</p>
            <p className="font-mono text-sm">
              <strong className="text-white">SIGNAPHARMA FRANCE</strong> — marque de<br />
              <strong className="text-white">LOSTA TECH SAS</strong><br />
              Société par actions simplifiée à associé unique<br />
              Capital social : 500 €<br />
              1103 Grand Route<br />
              01630 Saint-Jean-de-Gonville — France<br />
              SIREN : 103 764 270 · RCS Bourg-en-Bresse : 103 764 270<br />
              TVA intracommunautaire : FR34103764270<br />
              EUID : FR0101.103764270<br />
              Président : LOSTA Group<br />
              Téléphone : <a href="tel:+33610443864" className="text-[#39FF14] hover:underline">+33 6 10 44 38 64</a><br />
              E-mail : <a href="mailto:info@signapharma.fr" className="text-[#39FF14] hover:underline">info@signapharma.fr</a>
            </p>
            <p>
              Signapharma France est spécialisé dans la fabrication et la vente d'enseignes lumineuses,
              lettres lumineuses, croix de pharmacie LED, logos rétroéclairés et signalétique sur mesure.
            </p>
          </Section>

          <Section n={2} title="Objet">
            <p>
              Les présentes Conditions Générales de Vente définissent les droits et obligations
              entre LOSTA TECH SAS (marque Signapharma France) et toute personne physique ou morale
              effectuant un achat via le site internet www.signapharma.fr.
            </p>
            <p>Toute commande implique l'acceptation pleine et entière des présentes CGV.</p>
          </Section>

          <Section n={3} title="Produits">
            <p>Les produits proposés sont fabriqués sur mesure ou personnalisés selon les demandes du client :</p>
            <Bullets items={[
              "Croix de pharmacie lumineuses",
              "Lettres boîtiers LED",
              "Logos lumineux",
              "Enseignes intérieures et extérieures",
              "Caducées lumineux",
              "Signalétique professionnelle",
              "Néons LED personnalisés",
            ]} />
            <p>
              Les visuels, dimensions et couleurs présentés sur le site sont non contractuels et
              peuvent légèrement varier selon les matériaux, écrans ou contraintes techniques de
              fabrication.
            </p>
          </Section>

          <Section n={4} title="Commande">
            <p>Toute commande devient définitive après :</p>
            <Bullets items={[
              "Validation du panier par le client",
              "Acceptation des présentes Conditions Générales de Vente",
              "Réception et validation du paiement",
            ]} />
            <p>Une confirmation de commande est envoyée par e-mail après validation.</p>
            <p>Signapharma France se réserve le droit de refuser ou d'annuler toute commande en cas :</p>
            <Bullets items={[
              "D'informations incomplètes ou erronées",
              "De problème de paiement",
              "D'impossibilité technique de fabrication",
              "De suspicion de fraude ou litige antérieur",
            ]} />
            <p>
              Les maquettes, aperçus et visuels transmis au client doivent être vérifiés avant
              validation définitive de fabrication.
            </p>
          </Section>

          <Section n={5} title="Prix et TVA">
            <p>Tous les prix affichés sur le site sont indiqués exclusivement en euros (€).</p>
            <p>
              Les prix sont affichés hors taxes (HT). La TVA française au taux normal de <strong>20 %</strong> est
              ajoutée sur la facture finale (LOSTA TECH SAS, SIREN 103 764 270).
            </p>
            <p>
              Les frais de livraison, d'installation ou prestations complémentaires éventuelles
              sont indiqués séparément avant validation finale de la commande.
            </p>
            <p>
              Signapharma France se réserve le droit de modifier ses tarifs à tout moment, sans impact
              sur les commandes déjà validées et payées.
            </p>
          </Section>

          <Section n={6} title="Paiement">
            <p>Les paiements peuvent être effectués par :</p>
            <Bullets items={[
              "Carte bancaire (Visa, Mastercard) via Stripe — paiement en ligne sécurisé",
              "Virement bancaire (IBAN : FR76 1027 8073 6300 0232 9420 474 — BIC : CMCIFR2A)",
              "Solutions de paiement partenaires (sur devis)",
            ]} />
            <p>Selon le type de fabrication, un acompte peut être exigé avant lancement de production.</p>
            <p>
              En cas de retard de paiement, application d'une pénalité au taux d'intérêt de la BCE
              majoré de 10 points et d'une indemnité forfaitaire pour frais de recouvrement de 40 €
              (Art. L441-10 et D441-5 du Code de commerce).
            </p>
          </Section>

          <Section n={7} title="Fabrication et délais">
            <p>
              Les produits étant majoritairement fabriqués sur mesure, les délais de fabrication
              et d'expédition sont généralement compris entre :
            </p>
            <div className="my-4 inline-block px-5 py-3 border border-[#39FF14]/40 bg-[#39FF14]/5">
              <span className="font-display text-2xl font-bold text-[#39FF14] neon-text-soft">
                3 à 5 semaines
              </span>
            </div>
            <p>Les délais sont donnés à titre indicatif et peuvent varier selon :</p>
            <Bullets items={[
              "La complexité du projet",
              "La validation des fichiers",
              "Les périodes de forte activité",
              "Les contraintes fournisseurs ou transporteurs",
            ]} />
            <p>Un retard raisonnable ne peut donner lieu à annulation, remboursement ou indemnisation.</p>
          </Section>

          <Section n={8} title="Livraison">
            <p>Signapharma France expédie ses produits dans toute la France métropolitaine. Livraisons outre-mer et Union européenne sur devis.</p>
            <p>
              Les livraisons sont effectuées par transporteur spécialisé ou service postal selon
              les dimensions des produits.
            </p>
            <p>
              Le client est tenu de vérifier l'état du colis à réception et de signaler toute
              anomalie sous 48 heures avec photos à l'appui.
            </p>
            <p>Les risques liés au transport sont transférés au client dès la livraison.</p>
          </Section>

          <Section n={9} title="Installation">
            <p>Sauf mention contraire, les produits sont vendus sans pose.</p>
            <p>Le client est responsable :</p>
            <Bullets items={[
              "Des autorisations administratives (déclaration ou autorisation d'enseigne en mairie — art. R581-1 et suivants du Code de l'environnement)",
              "Des raccordements électriques (respect NF C 15-100)",
              "Du respect des normes locales et du règlement local de publicité (RLP) éventuel",
              "De la conformité du support de fixation",
            ]} />
            <p>Toute installation doit être réalisée par un professionnel qualifié.</p>
            <p>
              <strong className="text-white">Éclairage nocturne :</strong> conformément à l'
              <strong className="text-[#39FF14]">article R.581-59 du Code de l'environnement</strong>, lorsque l'activité
              signalée a cessé, les enseignes lumineuses doivent en principe être éteintes entre 1 h et 6 h du matin.
              Des conditions particulières s'appliquent lorsque l'activité commence ou se termine entre minuit et 7 h.
              Les règles locales peuvent apporter des prescriptions complémentaires (règlement local de publicité).
            </p>
          </Section>

          <Section n={10} title="Droit de rétractation">
            <p>
              Conformément à l'article L221-28 du Code de la consommation, le droit de rétractation
              de 14 jours <strong>ne s'applique pas</strong> aux biens confectionnés selon les
              spécifications du consommateur ou nettement personnalisés (croix, lettres, enseignes
              sur mesure).
            </p>
            <p>
              Pour les rares produits standards non personnalisés, le consommateur dispose d'un
              délai de 14 jours à compter de la réception pour exercer son droit de rétractation,
              sans avoir à motiver sa décision ni à supporter d'autres coûts que ceux prévus par
              la loi. Les frais de retour restent à la charge du client.
            </p>
          </Section>

          <Section n={11} title="Garantie légale">
            <p>
              Les produits bénéficient de la garantie légale de conformité (articles L217-3 et
              suivants du Code de la consommation, 2 ans) et de la garantie contre les vices cachés
              (articles 1641 et suivants du Code civil).
            </p>
            <p>
              Une garantie commerciale supplémentaire couvre les défauts de fabrication dans le
              cadre d'une utilisation normale. La garantie ne couvre pas :
            </p>
            <Bullets items={[
              "Mauvaise installation",
              "Choc ou casse",
              "Surtension électrique",
              "Humidité excessive",
              "Modification du produit",
              "Usure normale",
            ]} />
            <p>Les LED ont une durée de vie indicative pouvant atteindre 50 000 heures selon utilisation.</p>
          </Section>

          <Section n={12} title="Produits personnalisés">
            <p>
              Les produits personnalisés ou fabriqués sur mesure ne sont ni repris ni remboursés
              après validation de fabrication (voir article 10 — droit de rétractation).
            </p>
            <p>Le client est responsable de la conformité des fichiers, logos et textes transmis.</p>
          </Section>

          <Section n={13} title="Propriété intellectuelle">
            <p>
              Les maquettes, visuels, concepts et créations réalisés par Signapharma France restent
              la propriété exclusive de LOSTA TECH SAS jusqu'au paiement intégral des sommes dues.
            </p>
            <p>Toute reproduction ou utilisation sans autorisation écrite est interdite.</p>
          </Section>

          <Section n={14} title="Responsabilité">
            <p>Signapharma France ne pourra être tenu responsable :</p>
            <Bullets items={[
              "Des pertes d'exploitation",
              "Des retards liés aux transporteurs",
              "Des dommages indirects",
              "D'une mauvaise utilisation des produits",
            ]} />
            <p>La responsabilité totale de LOSTA TECH SAS est limitée au montant de la commande concernée.</p>
          </Section>

          <Section n={15} title="Données personnelles (RGPD)">
            <p>
              Les informations collectées sont utilisées uniquement dans le cadre du traitement des
              commandes et de la relation commerciale. Elles sont conservées pour la durée nécessaire
              aux finalités poursuivies (comptabilité : 10 ans conformément au Code de commerce).
            </p>
            <p>
              Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi
              Informatique et Libertés, vous disposez d'un droit d'accès, de rectification,
              d'effacement, de portabilité, de limitation et d'opposition sur vos données. Pour
              exercer ces droits : <a href="mailto:info@signapharma.fr" className="text-[#39FF14] hover:underline">info@signapharma.fr</a>.
            </p>
            <p>
              Vous pouvez également introduire une réclamation auprès de la CNIL (www.cnil.fr).
            </p>
          </Section>

          <Section n={16} title="Médiation de la consommation">
            <p>
              Conformément à l'article L612-1 du Code de la consommation, en cas de litige non
              résolu à l'amiable, le consommateur peut recourir gratuitement à un médiateur de la
              consommation. Une plateforme européenne de règlement en ligne des litiges est
              également disponible : <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-[#39FF14] hover:underline">ec.europa.eu/consumers/odr</a>.
            </p>
          </Section>

          <Section n={17} title="Droit applicable et juridiction">
            <p>Les présentes CGV sont soumises au droit français.</p>
            <p>
              En cas de litige et à défaut de résolution amiable, les tribunaux compétents seront
              ceux du ressort de <strong>Bourg-en-Bresse</strong> (Ain), sauf disposition légale
              impérative contraire (notamment en cas de litige avec un consommateur, qui peut
              saisir la juridiction de son domicile).
            </p>
          </Section>

          {/* Footer date */}
          <div className="mt-16 pt-8 border-t border-white/10 font-mono text-xs uppercase tracking-widest text-zinc-500 flex flex-col sm:flex-row items-center justify-between gap-3">
            <span>Signapharma France — LOSTA TECH SAS · SIREN 103 764 270</span>
            <span>CGV applicables — version {new Date().getFullYear()}</span>
          </div>
        </div>
      </section>
    </div>
  );
}
